import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { RevenueReportAggregateEnum } from 'anchor-scout-common/types';
import { Button, Page, PageContentHeader, Spinner, TableList, useStateFromUrl } from 'seagull';
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import { routes } from "../../../constants";
import { useIoContext, useStore, useStoreActions } from "../../../contexts";
import { useOnMount } from "../../../hooks/useOnMount";
import { useSortFromUrl } from "../../../hooks/useSortFromUrl";
import { selectors } from "../../../store/state-selectors";
import { formatLongMonthUSDate, formatNumericUSDate } from "../../../utils";
import { calculateStartDate, calculateEndDate } from "../components/DateRangeWithPresets/DateRangeWithPresets.utils";
import TableFilters from "../components/TableFilters/TableFilters";
import { DateRange } from "../components/types";
import { defaultAggregate, filterLabels, getDefaultSort, getHeaderItems, makeCsv, makeFiltersConfig, revenueReportColumnDefs, revenueReportHeaderColumnDefs } from "./RevenueBreakdown.utils";
import { RevenueBreakdownRow } from "./RevenueBreakdownRow";
import { Breakdown, FilterType } from "./types";
import { useSetDocumentTitle } from "../../../hooks/useSetDocumentTitle";
import styles from "./RevenueBreakdown.module.scss";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const testIdPrefix = 'revenue-breakdown-report';
const title = 'Revenue Breakdown Report';
export function RevenueBreakdownReport(_ref) {
  let {
    now
  } = _ref;
  const history = useHistory();
  const {
    REVENUE_REPORT_V2
  } = useFeatureFlags();
  const [revenueBreakdownReportData, setRevenueBreakdownReportData] = useState([]);
  const {
    urlState: {
      dateRange,
      dateRangeOption
    },
    changeUrlState
  } = useStateFromUrl({
    dateRange: {
      defaultValue: [calculateStartDate(formatNumericUSDate(now), DateRange.Last12Months, now), calculateEndDate(formatNumericUSDate(now), DateRange.Last12Months, now)],
      multi: true
    },
    dateRangeOption: {
      defaultValue: DateRange.Last12Months
    }
  });
  const [selectedFilters, setSelectedFilters] = useState({
    [FilterType.DateRange]: {
      dateRange,
      dateRangeOption
    },
    [FilterType.Aggregate]: defaultAggregate,
    [FilterType.Breakdowns]: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const {
    downloadCsv
  } = useIoContext();
  const breakdowns = useMemo(() => selectedFilters[FilterType.Breakdowns] || [], [selectedFilters]);
  const aggregate = useMemo(() => selectedFilters[FilterType.Aggregate] || defaultAggregate, [selectedFilters]);
  const headerItems = useMemo(() => getHeaderItems(breakdowns, aggregate), [aggregate, breakdowns]);
  const sortIds = compact(headerItems.map(item => item.sortId));
  const defaultSort = useMemo(() => getDefaultSort(sortIds), [sortIds]);
  const {
    sort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const {
    api
  } = useStoreActions();
  const businessName = useStore(selectors.user.businessName);
  const handleFetchFilterConfig = async () => {
    const [services, customers] = await Promise.all([REVENUE_REPORT_V2 ? api.reports.revenueReportServicesV2() : api.reports.revenueReportServices(), REVENUE_REPORT_V2 ? api.reports.revenueReportCustomersV2() : api.reports.revenueReportCustomers()]);
    const filterOptions = {
      [FilterType.ServiceName]: services,
      [FilterType.CustomerName]: customers
    };
    return makeFiltersConfig(filterOptions);
  };
  const showPeriod = aggregate !== RevenueReportAggregateEnum.AllTime;
  const showCustomer = breakdowns.includes(Breakdown.Customer);
  const showService = breakdowns.includes(Breakdown.Service);
  const selectedDateRange = selectedFilters[FilterType.DateRange];
  useEffect(() => {
    selectedDateRange && changeUrlState(selectedDateRange);
  }, [selectedDateRange]);
  const fetchRevenueBreakdownReport = (selectedFilters, sort) => {
    var _selectedFilters$Filt, _selectedFilters$Filt2;
    setIsLoading(true);
    const revenueReportFn = REVENUE_REPORT_V2 ? api.reports.revenueReportV2 : api.reports.revenueReport;
    revenueReportFn({
      start: (_selectedFilters$Filt = selectedFilters[FilterType.DateRange]) === null || _selectedFilters$Filt === void 0 ? void 0 : _selectedFilters$Filt.dateRange[0],
      end: (_selectedFilters$Filt2 = selectedFilters[FilterType.DateRange]) === null || _selectedFilters$Filt2 === void 0 ? void 0 : _selectedFilters$Filt2.dateRange[1],
      breakdowns: selectedFilters[FilterType.Breakdowns] || [],
      aggregate: selectedFilters[FilterType.Aggregate],
      customers: selectedFilters[FilterType.CustomerName] || [],
      services: selectedFilters[FilterType.ServiceName] || [],
      sort: sort ? [sortIds[sort.colIndex], sort.sort] : undefined
    }).then(data => setRevenueBreakdownReportData(data)).finally(() => setIsLoading(false));
  };
  useSetDocumentTitle(title);
  useOnMount(() => {
    fetchRevenueBreakdownReport(selectedFilters, sort);
  });
  const onExport = () => downloadCsv(makeCsv({
    headerRow: revenueReportHeaderColumnDefs[breakdowns.join(',')](aggregate).map(_ref2 => {
      let {
        value
      } = _ref2;
      return value;
    }),
    entries: revenueBreakdownReportData,
    showPeriod,
    showCustomer,
    showService
  }), "".concat(businessName, "_revenue_report_").concat(formatLongMonthUSDate(new Date()), ".csv"));
  const onSortChange = sort => {
    changeSort(sort);
    fetchRevenueBreakdownReport(selectedFilters, sort);
  };
  return /*#__PURE__*/_jsx(Page, {
    "data-testid": testIdPrefix,
    header: title,
    onClickBack: () => history.push(routes.reports),
    actions: /*#__PURE__*/_jsx(Button, {
      skin: "primary",
      "data-testid": "".concat(testIdPrefix, "-export-btn"),
      onClick: onExport,
      children: "Export"
    }),
    children: /*#__PURE__*/_jsxs(TableList.Provider, {
      columnDefs: revenueReportColumnDefs[breakdowns.join(',')](aggregate),
      numRows: revenueBreakdownReportData.length,
      sort: sort,
      onSortChange: onSortChange,
      children: [/*#__PURE__*/_jsx(PageContentHeader, {
        children: /*#__PURE__*/_jsx("div", {
          className: styles.pageContent,
          children: /*#__PURE__*/_jsx(RestfulWrapper, {
            fetch: handleFetchFilterConfig,
            renderLoader: () => /*#__PURE__*/_jsx(Spinner, {
              size: 24
            }),
            renderChild: _ref3 => {
              let {
                data: filterConfig
              } = _ref3;
              return /*#__PURE__*/_jsx(TableFilters, {
                testIdPrefix: testIdPrefix,
                filterLabels: filterLabels,
                filterConfig: filterConfig,
                selectedFilters: selectedFilters,
                setSelectedFilters: setSelectedFilters,
                onRefresh: fetchRevenueBreakdownReport
              });
            }
          })
        })
      }), /*#__PURE__*/_jsx(TableList.Structure, {
        header: /*#__PURE__*/_jsx(TableList.Header, {
          stickyToTop: true,
          items: headerItems,
          "data-testid": "".concat(testIdPrefix, "-table-header")
        }),
        content: isLoading ? /*#__PURE__*/_jsx(Spinner, {
          centeredInBox: true
        }) : /*#__PURE__*/_jsx(_Fragment, {
          children: revenueBreakdownReportData.map((reportRow, index) => /*#__PURE__*/_jsx(RevenueBreakdownRow, {
            index: index,
            day: reportRow.formattedDay,
            customer: reportRow.customer,
            service: reportRow.service,
            revenue: reportRow.revenue,
            breakdowns: breakdowns,
            showPeriod: showPeriod,
            showCustomer: showCustomer,
            showService: showService
          }, "".concat(reportRow.formattedDay, "-").concat(reportRow.customer, "-").concat(reportRow.service)))
        })
      })]
    })
  });
}